import {buildAnchor, isAnchoredInLang, resolveAnchor} from './annotationAnchors';
import type {TextProjection} from './annotationTextProjection';
import type {Annotation, AnnotationMerge, AnnotationRange} from './annotationTypes';

interface ResolvedAnnotation {
  annotation: Annotation;
  resolved: AnnotationRange;
}

/**
 * The marks the selection strictly overlaps (touching doesn't count), sorted
 * by position. Candidates must match `lang` and currently resolve — a dropped
 * anchor's offsets can't be trusted.
 */
function findOverlappingAnnotations(
  projection: TextProjection,
  annotations: Annotation[],
  range: AnnotationRange,
  lang: string | null
): ResolvedAnnotation[] {
  const overlapping: ResolvedAnnotation[] = [];
  annotations.forEach((annotation) => {
    if (!isAnchoredInLang(annotation.anchor, lang)) {
      return;
    }
    const resolved = resolveAnchor(annotation.anchor, projection);
    if (!resolved || range.start >= resolved.end || range.end <= resolved.start) {
      return;
    }
    overlapping.push({annotation, resolved});
  });
  return overlapping.sort((a, b) => a.resolved.start - b.resolved.start);
}

/**
 * The lone mark that already covers the whole selection — such a selection adds
 * nothing, so callers drop it and keep the mark (#19010). Null when several
 * marks overlap the selection even if one of them contains it: those marks
 * overlap each other, and merging them heals that, so it is not a no-op.
 */
export function findContainingAnnotation(
  projection: TextProjection,
  annotations: Annotation[],
  range: AnnotationRange,
  lang: string | null
): Annotation | null {
  const overlapping = findOverlappingAnnotations(projection, annotations, range, lang);
  if (overlapping.length !== 1) {
    return null;
  }
  const [{annotation, resolved}] = overlapping;
  const contains = resolved.start <= range.start && range.end <= resolved.end;
  return contains ? annotation : null;
}

/**
 * Trimming is seam hygiene for the join, so a survivor with nothing to absorb
 * keeps the text the student typed, verbatim.
 */
export function combineNotes(
  survivorNote: string | undefined,
  absorbedNotes: (string | undefined)[]
): string | undefined {
  const absorbed = absorbedNotes
    .map((note) => note?.trim())
    .filter((note): note is string => Boolean(note));
  if (absorbed.length === 0) {
    return survivorNote?.trim() ? survivorNote : undefined;
  }
  const survivor = survivorNote?.trim();
  return survivor ? [survivor, ...absorbed].join('\n\n') : absorbed.join('\n\n');
}

/** The note a merge would produce. */
export function combinedNoteOf(
  annotations: Annotation[],
  {intoId, absorbedIds}: Pick<AnnotationMerge, 'intoId' | 'absorbedIds'>
): string | undefined {
  const survivor = annotations.find((annotation) => annotation.id === intoId);
  const absorbed = absorbedIds.map(
    (absorbedId) => annotations.find((annotation) => annotation.id === absorbedId)?.note
  );
  return combineNotes(survivor?.note, absorbed);
}

/**
 * Merge policy for a selection overlapping existing marks (#19010): they join
 * into one span and the earliest-by-position mark survives, keeping its id and
 * style. Returns the ready-to-apply `mergeAnnotations` store update. Null when
 * nothing overlaps (plain add). Check `findContainingAnnotation` first — a
 * contained selection needs no update.
 */
export function computeAnnotationMerge(
  projection: TextProjection,
  annotations: Annotation[],
  range: AnnotationRange,
  lang: string | null
): AnnotationMerge | null {
  const overlapping = findOverlappingAnnotations(projection, annotations, range, lang);
  if (overlapping.length === 0) {
    return null;
  }
  const [{annotation: into, resolved: intoRange}] = overlapping;
  const union = {
    start: Math.min(range.start, intoRange.start),
    end: Math.max(range.end, ...overlapping.map(({resolved}) => resolved.end))
  };
  const anchor = buildAnchor(projection, union, {regionId: into.anchor.regionId, lang});
  if (!anchor) {
    return null;
  }
  return {
    intoId: into.id,
    absorbedIds: overlapping.slice(1).map(({annotation}) => annotation.id),
    anchor
  };
}
